import { getAutomation, Automation } from '@kizen/api/automation';
import {
  type UseQueryOptions,
  useQuery,
  useQueries,
} from '@tanstack/react-query';
import AxiosService from '__services/AxiosService';
import { AUTOMATIONS } from '../query-keys';
import { useMemo } from 'react';

export type UseAutomationQueryResponse = Automation;

export const useAutomationQuery = <TData = UseAutomationQueryResponse>(
  automationId: string,
  options: Omit<
    UseQueryOptions<UseAutomationQueryResponse, unknown, TData>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  return useQuery({
    queryKey: AUTOMATIONS.AUTOMATION(automationId),
    queryFn: async () => {
      return getAutomation(AxiosService, automationId);
    },
    ...options,
  });
};

export const useAutomationQueries = <TData = UseAutomationQueryResponse>(
  automationIds: Array<string>,
  options: Omit<
    UseQueryOptions<UseAutomationQueryResponse, unknown, TData>,
    'queryKey' | 'queryFn'
  > = {}
) => {
  const queries = useMemo(
    () =>
      automationIds.map((automationId) => ({
        queryKey: AUTOMATIONS.AUTOMATION(automationId),
        queryFn: async () => {
          return getAutomation(AxiosService, automationId);
        },
        ...options,
        refetchOnWindowFocus: true,
        staleTime: 0,
      })),
    [automationIds, options]
  );

  return useQueries({ queries });
};
